import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { ArrowLeft } from 'lucide-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { APP_CONFIG } from '@/config/app.config';
import { buildEncodedUrl, isValidHostname, slugifyForFilename } from './siteQRCode.utils';

export type SiteQRCodeProps = {
  subdomain: string;
  customDomains: string[];
};

const buildSubdomainUrl = (subdomain: string): string =>
  `https://${subdomain}.${APP_CONFIG.SITES_DOMAIN}`;

const buildCustomDomainUrl = (domain: string): string => `https://${domain}`;

export const SiteQRCode: React.FC<SiteQRCodeProps> = ({ subdomain, customDomains }) => {
  // Validate every hostname before building URLs from it — custom domains
  // are free-form user input, and an invalid entry (e.g. "my domain.com")
  // would make the render-time `new URL(...)` calls below throw. Invalid
  // entries are skipped rather than rendered.
  const urls = useMemo(() => {
    const list: string[] = [];
    if (subdomain && isValidHostname(`${subdomain}.${APP_CONFIG.SITES_DOMAIN}`)) {
      list.push(buildSubdomainUrl(subdomain));
    }
    for (const d of customDomains) {
      if (isValidHostname(d)) list.push(buildCustomDomainUrl(d));
    }
    return list;
  }, [subdomain, customDomains]);

  const isMulti = urls.length > 1;

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'pick' | 'qr'>(isMulti ? 'pick' : 'qr');
  const [selectedUrl, setSelectedUrl] = useState<string | null>(
    isMulti ? null : urls[0] ?? null,
  );
  const [renderError, setRenderError] = useState(false);
  // Track the canvas via state, not a ref, so the draw effect re-runs the
  // moment the popover content (and the canvas with it) actually mounts.
  // Radix portals the popover, so the canvas doesn't exist in the DOM until
  // `open` flips true — a useRef would never re-trigger the effect once
  // encodedUrl was already set, leaving the canvas blank.
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);

  // Reset to initial state on close so reopening is predictable.
  useEffect(() => {
    if (open) return;
    setStep(isMulti ? 'pick' : 'qr');
    setSelectedUrl(isMulti ? null : urls[0] ?? null);
    setRenderError(false);
  }, [open, isMulti, urls]);

  const encodedUrl = selectedUrl ? buildEncodedUrl(selectedUrl) : null;

  useEffect(() => {
    if (!encodedUrl || !canvas) return;
    setRenderError(false);
    QRCode.toCanvas(canvas, encodedUrl, {
      width: 1024,
      errorCorrectionLevel: 'M',
    })
      .then(() => {
        // qrcode.toCanvas writes inline `style="width:1024px;height:1024px"`
        // imperatively after the draw, which overrides our Tailwind sizing
        // and overflows the popover. Clear it so CSS owns layout.
        canvas.style.width = '';
        canvas.style.height = '';
      })
      .catch(() => {
        setRenderError(true);
      });
  }, [encodedUrl, canvas]);

  if (urls.length === 0) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const handleSelect = (url: string) => {
    setSelectedUrl(url);
    setStep('qr');
  };

  const handleBack = () => {
    setStep('pick');
  };

  const handleDownload = () => {
    if (!canvas || !selectedUrl) return;
    const dataUrl = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${slugifyForFilename(selectedUrl)}-qr.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="dialog"
          className="text-sm text-foreground underline decoration-dotted underline-offset-4 hover:decoration-solid"
        >
          Generate…
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72">
        {isMulti && step === 'pick' && (
          <div className="flex flex-col gap-1">
            <div className="px-1 pb-2 text-xs uppercase tracking-wider text-background/60">
              Pick a URL
            </div>
            {urls.map((url) => (
              <button
                key={url}
                type="button"
                onClick={() => handleSelect(url)}
                className="rounded px-2 py-2 text-left text-sm font-medium leading-tight hover:bg-background/10 break-all"
              >
                {new URL(url).hostname}
              </button>
            ))}
          </div>
        )}

        {step === 'qr' && selectedUrl && (
          <div className="flex flex-col gap-3">
            {isMulti && (
              <button
                type="button"
                onClick={handleBack}
                className="self-start inline-flex items-center gap-1 text-xs text-background/60 hover:text-background"
              >
                <ArrowLeft className="h-3 w-3 stroke-[1.5]" />
                Back
              </button>
            )}
            <div className="text-xs text-background/60 break-all">
              for: {new URL(selectedUrl).hostname}
            </div>
            {renderError ? (
              <div className="text-sm text-background/80">
                Couldn&apos;t generate QR — try again later.
              </div>
            ) : (
              <div className="rounded-2xl bg-background p-2">
                {/*
                  The wrapper owns the layout: aspect-square + w-full forces
                  a defined box that's sized by the popover's content area.
                  The canvas is positioned absolutely inside and stretched
                  to fill the box via inset-0 + h-full + w-full. Without
                  this wrapper, the canvas's HTML width="1024" attribute
                  (set by qrcode.toCanvas for download resolution) competes
                  with CSS sizing and can overflow the popover.
                */}
                <div className="relative w-full aspect-square">
                  <canvas
                    ref={setCanvas}
                    data-testid="qr-canvas"
                    className="absolute inset-0 h-full w-full"
                  />
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={handleDownload}
              disabled={renderError}
              className="inline-flex h-9 items-center justify-center rounded-full bg-background px-4 text-sm font-medium text-foreground hover:bg-background/90 active:bg-background/75 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Download PNG
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default SiteQRCode;
